// Servizio per la gestione delle agende

import apiClient from './apiClient';
import { Diary, ApiResponse } from '../types';

// Interfaccia per i parametri di ricerca
export interface DiarySearchParams {
    execution_unit_id?: string;
    hospital_id?: string;
    active_only?: boolean;
    online_only?: boolean;
    access_type_id?: number;
    limit?: number;
}

// Interfaccia per le statistiche
export interface DiaryStats {
    status_id: number;
    status_name: string;
    count: number;
}

// Interfaccia per la sincronizzazione
export interface SyncOptions {
    force_overwrite?: boolean;
}

export interface SyncResult {
    type: string;
    processed: number;
    created: number;
    updated: number;
    skipped: number;
    errors: number;
    duration: number;
    timestamp: string;
}

// Interfaccia per le opzioni di cancellazione
export interface DeleteOptions {
    type: 'soft' | 'hard';
    reason?: string;
    isHardDelete: boolean;
}

// Risultato della cancellazione
export interface DeleteResult {
    success: boolean;
    message: string;
    details?: {
        local_deleted: boolean;
        recup_deleted: boolean;
        recup_id?: string | null;
    };
    note?: string;
}

// Funzione per recuperare tutte le agende
export const getDiaries = async (params?: DiarySearchParams): Promise<Diary[]> => {
    try {
        const response = await apiClient.get<ApiResponse<Diary[]>>('/diaries', { params });
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero delle agende:', error);
        // Restituiamo un array vuoto in caso di errore per evitare crash
        return [];
    }
};

// Funzione per recuperare un'agenda specifica
export const getDiary = async (id: string): Promise<Diary> => {
    try {
        const response = await apiClient.get<ApiResponse<Diary>>(`/diaries/${id}`);
        if (response.data && 'data' in response.data) {
            return response.data.data;
        }
        throw new Error('Agenda non trovata');
    } catch (error) {
        console.error('Errore durante il recupero dell\'agenda:', error);
        throw error;
    }
};

// Funzione per recuperare un'agenda per codice
export const getDiaryByCode = async (code: string): Promise<Diary> => {
    try {
        const response = await apiClient.get<ApiResponse<Diary>>(`/diaries/code/${code}`);
        if (response.data && 'data' in response.data) {
            return response.data.data;
        }
        throw new Error('Agenda non trovata');
    } catch (error) {
        console.error('Errore durante il recupero dell\'agenda per codice:', error);
        throw error;
    }
};

// Funzione per creare una nuova agenda
export const createDiary = async (diary: Omit<Diary, 'id' | 'createdAt' | 'updatedAt'>): Promise<Diary> => {
    try {
        const response = await apiClient.post<ApiResponse<Diary>>('/diaries', diary);
        if (response.data && 'data' in response.data) {
            return response.data.data;
        }
        throw new Error('Errore nella creazione dell\'agenda');
    } catch (error) {
        console.error('Errore durante la creazione dell\'agenda:', error);
        throw error;
    }
};

// Funzione per aggiornare un'agenda
export const updateDiary = async (id: string, diary: Partial<Diary>): Promise<Diary> => {
    try {
        const response = await apiClient.put<ApiResponse<Diary>>(`/diaries/${id}`, diary);
        if (response.data && 'data' in response.data) {
            return response.data.data;
        }
        throw new Error('Errore nell\'aggiornamento dell\'agenda');
    } catch (error) {
        console.error('Errore durante l\'aggiornamento dell\'agenda:', error);
        throw error;
    }
};

// Funzione per eliminare un'agenda (soft o hard delete)
export const deleteDiary = async (id: string, options: DeleteOptions): Promise<DeleteResult> => {
    try {
        const params = new URLSearchParams();
        if (options.isHardDelete) {
            params.append('hard_delete', 'true');
        }

        const response = await apiClient.delete<DeleteResult>(`/diaries/${id}?${params.toString()}`, {
            data: options.reason ? { reason: options.reason } : undefined
        });
        
        return response.data;
    } catch (error) {
        console.error('Errore durante l\'eliminazione dell\'agenda:', error);
        throw error;
    }
};

// Backward compatibility: funzione legacy per soft delete
export const deleteDiaryLegacy = async (id: string, reason?: string): Promise<void> => {
    await deleteDiary(id, {
        type: 'soft',
        reason,
        isHardDelete: false
    });
};

// Funzione per ripristinare un'agenda eliminata
export const restoreDiary = async (id: string): Promise<void> => {
    try {
        await apiClient.post(`/diaries/${id}/restore`);
    } catch (error) {
        console.error('Errore durante il ripristino dell\'agenda:', error);
        throw error;
    }
};

// Funzione per ottenere agende eliminate
export const getDeletedDiaries = async (): Promise<Diary[]> => {
    try {
        const response = await apiClient.get<ApiResponse<Diary[]>>('/diaries/deleted/list');
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero delle agende eliminate:', error);
        return [];
    }
};

// Funzione per ottenere audit trail di un'agenda
export const getDiaryAuditTrail = async (id: string): Promise<any[]> => {
    try {
        const response = await apiClient.get<ApiResponse<any[]>>(`/diaries/${id}/audit`);
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero dell\'audit trail:', error);
        return [];
    }
};

// Funzione per ottenere statistiche per stato
export const getDiaryStatsByStatus = async (): Promise<DiaryStats[]> => {
    try {
        const response = await apiClient.get<ApiResponse<DiaryStats[]>>('/diaries/stats/by-status');
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero delle statistiche:', error);
        return [];
    }
};

// Funzione per ottenere agende con disponibilità
export const getDiariesWithAvailability = async (fromDate: string, toDate: string): Promise<Diary[]> => {
    try {
        const response = await apiClient.get<ApiResponse<Diary[]>>('/diaries/with-availability', {
            params: { from_date: fromDate, to_date: toDate }
        });
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero delle agende con disponibilità:', error);
        return [];
    }
};

// Funzione per sincronizzare da RECUP
export const syncDiariesFromRecup = async (options: SyncOptions = {}): Promise<SyncResult> => {
    try {
        const response = await apiClient.post<ApiResponse<SyncResult>>('/diaries/sync/recup', options);
        if (response.data && 'data' in response.data) {
            return response.data.data;
        }
        throw new Error('Errore nella sincronizzazione');
    } catch (error) {
        // Gestisce il caso di conferma richiesta
        if (error.response?.status === 409) {
            throw {
                ...error,
                confirmationRequired: true,
                existingCount: error.response.data.existing_count
            };
        }
        console.error('Errore durante la sincronizzazione da RECUP:', error);
        throw error;
    }
};

// Funzione per sincronizzare una singola agenda da RECUP
export const syncSingleDiaryFromRecup = async (id: string, options: SyncOptions = {}): Promise<SyncResult> => {
    try {
        const response = await apiClient.post<ApiResponse<SyncResult>>(`/diaries/${id}/sync/recup`, options);
        if (response.data && 'data' in response.data) {
            return response.data.data;
        }
        throw new Error('Errore nella sincronizzazione singola agenda');
    } catch (error) {
        console.error('Errore durante la sincronizzazione singola agenda da RECUP:', error);
        throw error;
    }
};

// Funzione per ottenere statistiche di sincronizzazione
export const getSyncStats = async (): Promise<any> => {
    try {
        const response = await apiClient.get<ApiResponse<any>>('/diaries/sync/stats');
        return response.data.data;
    } catch (error) {
        console.error('Errore durante il recupero delle statistiche di sincronizzazione:', error);
        throw error;
    }
};

// Funzione per verificare lo stato di salute della sincronizzazione
export const getSyncHealth = async (): Promise<any> => {
    try {
        const response = await apiClient.get<ApiResponse<any>>('/diaries/sync/health');
        return response.data.data;
    } catch (error) {
        console.error('Errore durante il controllo dello stato di salute:', error);
        throw error;
    }
};

// Funzione per ottenere servizi per unità
export const getServicesByUnit = async (unitId: string): Promise<any[]> => {
    try {
        const response = await apiClient.get(`/healthcare-services/by-unit/${unitId}`);
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero dei servizi per unità:', error);
        return [];
    }
};

// Funzione per ottenere dottori per unità
export const getDoctorsByUnit = async (unitId: string): Promise<any[]> => {
    try {
        const response = await apiClient.get(`/doctors/by-unit/${unitId}`);
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero dei dottori per unità:', error);
        return [];
    }
};

// Funzione per ottenere disponibilità
export const getAvailabilities = async (params: any): Promise<any[]> => {
    try {
        const response = await apiClient.get('/availabilities', { params });
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero delle disponibilità:', error);
        return [];
    }
};

// Oggetto servizio di default per compatibilità
const diaryService = {
    getDiaries,
    getDiary,
    getDiaryByCode,
    createDiary,
    updateDiary,
    deleteDiary,
    deleteDiaryLegacy,
    restoreDiary,
    getDeletedDiaries,
    getDiaryAuditTrail,
    getDiaryStatsByStatus,
    getDiariesWithAvailability,
    syncDiariesFromRecup,
    syncSingleDiaryFromRecup,
    getServicesByUnit,
    getDoctorsByUnit,
    getAvailabilities,
    getSyncStats,
    getSyncHealth
};

export { diaryService };
export default diaryService;
